import React from 'react';
import {
    Grid, TextField, MenuItem, InputAdornment,
    Typography, Box, FormControl, InputLabel, Select,
    Alert, Paper
} from '@mui/material';

function BasicInfo({ onChange, value }) {
    const handleChange = (field) => (event) => {
        const newValue = event.target.value;
        if (field.startsWith('roomDetails.')) {
            const roomDetailField = field.split('.')[1];
            onChange({
                ...value,
                roomDetails: {
                    ...value.roomDetails,
                    [roomDetailField]: newValue
                }
            });
        } else if (field === 'location.walkingMinutes') {
            onChange({
                ...value,
                location: {
                    ...value.location,
                    walkingMinutes: newValue
                }
            });
        } else {
            onChange({
                ...value,
                [field]: newValue
            });
        }
    };

    const availabilityOptions = [
        { value: 'flexible', label: '相談可能' },
        { value: 'short', label: '短期（半年以内）' },
        { value: 'medium', label: '中期（2年以内）' },
        { value: 'long', label: '長期（2年以上）' }
    ];

    const moveInTimingOptions = [
        { value: 'immediate', label: 'すぐに入居可' },
        { value: 'one_month', label: '1ヶ月後以降' },
        { value: 'three_months', label: '3ヶ月後以降' }
    ];

    // 間取りオプション
    const layoutOptions = [
        { value: '1R', label: '1R' },
        { value: '1K', label: '1K' },
        { value: '1DK', label: '1DK' },
        { value: '1LDK', label: '1LDK' },
        { value: '2K', label: '2K' },
        { value: '2DK', label: '2DK' },
        { value: '2LDK', label: '2LDK' },
        { value: '3K', label: '3K' },
        { value: '3DK', label: '3DK' },
        { value: '3LDK', label: '3LDK' },
        { value: '4K以上', label: '4K以上' }
    ];

    // 徒歩分数オプション
    const walkingMinutesOptions = Array.from({ length: 30 }, (_, i) => ({
        value: String(i + 1),
        label: `${i + 1}分`
    }));

    return (
        <Box sx={{ mt: 3 }}>
            <Typography variant="h6" gutterBottom>
                基本情報
            </Typography>
            <Grid container spacing={3}>
                {/* 徒歩分数 */}
                <Grid item xs={12} sm={6}>
                    <FormControl fullWidth required>
                        <InputLabel>駅からの徒歩分数</InputLabel>
                        <Select
                            value={value.location.walkingMinutes || ''}
                            onChange={handleChange('location.walkingMinutes')}
                            label="駅からの徒歩分数"
                        >
                            {walkingMinutesOptions.map((option) => (
                                <MenuItem key={option.value} value={option.value}>
                                    {option.label}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Grid>

                {/* 家賃 */}
                <Grid item xs={12} sm={6}>
                    <TextField
                        required
                        fullWidth
                        label="家賃"
                        type="number"
                        value={value.rentamount || ''}
                        onChange={handleChange('rentamount')}
                        InputProps={{
                            startAdornment: <InputAdornment position="start">¥</InputAdornment>,
                            endAdornment: <InputAdornment position="end">/月</InputAdornment>
                        }}
                    />
                </Grid>

                {/* 物件種別 */}
                <Grid item xs={12} sm={6}>
                    <FormControl fullWidth required>
                        <InputLabel>物件種別</InputLabel>
                        <Select
                            value={value.propertyType || ''}
                            onChange={handleChange('propertyType')}
                            label="物件種別"
                        >
                            {['アパート', 'マンション', '一戸建て', 'シェアハウス'].map((option) => (
                                <MenuItem key={option} value={option}>
                                    {option}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Grid>

                {/* 部屋タイプ */}
                <Grid item xs={12} sm={6}>
                    <FormControl fullWidth required>
                        <InputLabel>部屋タイプ</InputLabel>
                        <Select
                            value={value.roomType || ''}
                            onChange={handleChange('roomType')}
                            label="部屋タイプ"
                        >
                            {['完全個室', '個室無し'].map((option) => (
                                <MenuItem key={option} value={option}>
                                    {option}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Grid>

                {/* 入居可能時期 */}
                <Grid item xs={12} sm={6}>
                    <FormControl fullWidth required>
                        <InputLabel>入居可能タイミング</InputLabel>
                        <Select
                            value={value.moveInTiming || ''}
                            onChange={handleChange('moveInTiming')}
                            label="入居可能タイミング"
                        >
                            {moveInTimingOptions.map((option) => (
                                <MenuItem key={option.value} value={option.value}>
                                    {option.label}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Grid>

                {/* 入居期間タイプ */}
                <Grid item xs={12} sm={6}>
                    <FormControl fullWidth required>
                        <InputLabel>居住可能期間</InputLabel>
                        <Select
                            value={value.availabilityType || ''}
                            onChange={handleChange('availabilityType')}
                            label="居住可能期間"
                        >
                            {availabilityOptions.map((option) => (
                                <MenuItem key={option.value} value={option.value}>
                                    {option.label}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Grid>

                {/* 間取り */}
                <Grid item xs={12} sm={6}>
                    <FormControl fullWidth required>
                        <InputLabel>間取り</InputLabel>
                        <Select
                            value={value.roomDetails.layout || ''}
                            onChange={handleChange('roomDetails.layout')}
                            label="間取り"
                        >
                            {layoutOptions.map((option) => (
                                <MenuItem key={option.value} value={option.value}>
                                    {option.label}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Grid>

                {/* 広さ */}
                <Grid item xs={12} sm={6}>
                    <TextField
                        required
                        fullWidth
                        label="広さ"
                        type="number"
                        value={value.roomDetails.size || ''}
                        onChange={handleChange('roomDetails.size')}
                        InputProps={{
                            endAdornment: <InputAdornment position="end">㎡</InputAdornment>
                        }}
                        helperText="貸出対象の部屋の広さを入力してください（共用部分は含みません）"
                    />
                </Grid>

                {/* 説明文 */}
                <Grid item xs={12}>
                    <TextField
                        fullWidth
                        multiline
                        rows={4}
                        label="物件の説明"
                        value={value.description || ''}
                        onChange={handleChange('description')}
                        placeholder="物件の特徴や注意事項を詳しく入力してください"
                        helperText="詳細な情報を記載するほど、入居希望者とのマッチング率が上がります"
                    />
                    <Paper elevation={0} sx={{ mt: 2, p: 2, bgcolor: 'grey.50' }}>
                        <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                            👉 以下の項目を含めると物件の魅力が伝わりやすくなります
                        </Typography>
                        <Box component="ul" sx={{ m: 0, pl: 2 }}>
                            <Box component="li" sx={{ mb: 1 }}>
                                <Typography variant="body2" color="text.secondary">
                                    <strong>物件の特徴：</strong>日当たり、眺望、設備の状態、築年数、最近のリフォーム履歴など
                                </Typography>
                            </Box>
                            <Box component="li" sx={{ mb: 1 }}>
                                <Typography variant="body2" color="text.secondary">
                                    <strong>周辺環境：</strong>スーパー、コンビニ、公園、病院までの距離、治安、騒音状況など
                                </Typography>
                            </Box>
                            <Box component="li" sx={{ mb: 1 }}>
                                <Typography variant="body2" color="text.secondary">
                                    <strong>入居条件：</strong>保証人、初期費用、契約期間、更新料、ペット可否など
                                </Typography>
                            </Box>
                            <Box component="li" sx={{ mb: 1 }}>
                                <Typography variant="body2" color="text.secondary">
                                    <strong>共用部分：</strong>キッチン、バス、トイレ、ランドリーの使用ルール、清掃当番など
                                </Typography>
                            </Box>
                            <Box component="li">
                                <Typography variant="body2" color="text.secondary">
                                    <strong>その他特記事項：</strong>防音性、インターネット環境、セキュリティ、自転車置き場など
                                </Typography>
                            </Box>
                        </Box>
                    </Paper>
                </Grid>
            </Grid>
        </Box>
    );
}

export default BasicInfo; 